import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CalendarDays, CalendarRange, type LucideIcon } from 'lucide-react';
import { DetailPanel } from '@/components/common/DetailPanel';
import { ErrorState } from '@/components/common/ErrorState';
import { OptionSelect, type SelectOption } from '@/components/common/OptionSelect';
import { Skeleton } from '@/components/ui/skeleton';
import type { LeaveType } from '@/features/leaveTypes/types/leaveType.types';
import { leaveTypesQueryOptions } from '@/features/leaveTypes/api/leaveTypes.api';
import { cn } from '@/lib/utils';
import { REFETCH_FADE_TRANSITION_CLASS, REFETCHING_OPACITY_CLASS } from '@/styles/refetchFade';
import { getMonthOptions, getYearOptions } from '@/utils/date';
import { pluralize } from '@/utils/number';
import { leaveUsageSummaryQueryOptions } from '../api/leaveApplications.api';
import type {
  LeaveUsage,
  LeaveUsageSummary as LeaveUsageSummaryData,
} from '../types/leaveApplication.types';
import { getLeaveUsageCounts, type LeaveUsageCount } from '../utils/leaveUsageCounts';

const USAGE_CHIP_LIST_CLASS = 'flex flex-wrap gap-2';
const USAGE_CHIP_CLASS =
  'inline-flex items-baseline gap-1.5 rounded-xl px-3.5 py-2 text-sm text-muted-foreground bg-[linear-gradient(180deg,var(--color-card),color-mix(in_oklab,var(--color-primary)_6%,var(--color-card)))] shadow-[inset_0_0_0_1px_color-mix(in_oklab,var(--color-primary)_15%,transparent),0_6px_16px_-10px_color-mix(in_oklab,var(--color-primary)_55%,transparent)]';
const ZERO_COUNT_CHIP_CLASS = 'opacity-50';
const SKELETON_CHIPS = [
  { key: 'first', widthClass: 'w-36' },
  { key: 'second', widthClass: 'w-28' },
  { key: 'third', widthClass: 'w-24' },
  { key: 'fourth', widthClass: 'w-28' },
  { key: 'fifth', widthClass: 'w-40' },
  { key: 'sixth', widthClass: 'w-32' },
] as const;
const YEARS_BEFORE_THIS_YEAR = 5;
const YEARS_AFTER_THIS_YEAR = 1;
const NO_LEAVE_TYPES: LeaveType[] = [];
const MONTH_OPTIONS = getMonthOptions();
const PERIOD_SELECT_TRIGGER_CLASS =
  'w-auto cursor-pointer gap-1 rounded-lg border-transparent px-2.5 font-medium text-foreground transition-colors data-[size=default]:h-8 hover:bg-primary/8 focus:border-transparent focus:ring-0 focus-visible:ring-2 focus-visible:ring-ring/40 data-popup-open:bg-primary/10';

type UsagePeriod = 'year' | 'month';

interface UsagePeriodConfig {
  title: string;
  icon: LucideIcon;
  hasMonthPicker: boolean;
  getUsage: (summary: LeaveUsageSummaryData) => LeaveUsage;
}

const USAGE_PERIOD_CONFIG: Record<UsagePeriod, UsagePeriodConfig> = {
  year: {
    title: 'Yearly Summary',
    icon: CalendarRange,
    hasMonthPicker: false,
    getUsage: (summary) => summary.yearly,
  },
  month: {
    title: 'Monthly Summary',
    icon: CalendarDays,
    hasMonthPicker: true,
    getUsage: (summary) => summary.monthly,
  },
};

function getUsageYearOptions(shownYear: number | undefined) {
  const thisYear = new Date().getFullYear();
  const firstYear = Math.min(thisYear - YEARS_BEFORE_THIS_YEAR, shownYear ?? thisYear);
  const lastYear = Math.max(thisYear + YEARS_AFTER_THIS_YEAR, shownYear ?? thisYear);
  return getYearOptions(firstYear, lastYear);
}

interface PeriodPickerProps {
  id: string;
  label: string;
  options: readonly SelectOption[];
  value: number | undefined;
  onChange: (value: number) => void;
}

function PeriodPicker({ id, label, options, value, onChange }: PeriodPickerProps) {
  function handleChange(pickedValue: number | undefined) {
    if (pickedValue !== undefined) onChange(pickedValue);
  }

  return (
    <div>
      <label htmlFor={id} className="sr-only">
        {label}
      </label>
      <OptionSelect
        id={id}
        options={options}
        isLoading={value === undefined}
        value={value}
        onChange={handleChange}
        className={PERIOD_SELECT_TRIGGER_CLASS}
      />
    </div>
  );
}

interface UsageChipsProps {
  usage: LeaveUsage | undefined;
  leaveTypes: LeaveType[] | undefined;
  isRefreshing: boolean;
}

function UsageChips({ usage, leaveTypes, isRefreshing }: UsageChipsProps) {
  const counts = useMemo(
    () => (leaveTypes ? getLeaveUsageCounts(usage ? [usage] : [], leaveTypes) : undefined),
    [usage, leaveTypes],
  );

  if (!counts) {
    return (
      <div className={USAGE_CHIP_LIST_CLASS} aria-hidden>
        {SKELETON_CHIPS.map((chip) => (
          <Skeleton key={chip.key} className={cn('h-9 rounded-xl', chip.widthClass)} />
        ))}
      </div>
    );
  }

  return (
    <ul
      className={cn(
        USAGE_CHIP_LIST_CLASS,
        REFETCH_FADE_TRANSITION_CLASS,
        isRefreshing && REFETCHING_OPACITY_CLASS,
      )}
    >
      {counts.map((count) => (
        <UsageChip
          key={count.key}
          count={count}
          value={usage ? count.getCount(usage) : undefined}
        />
      ))}
    </ul>
  );
}

interface UsageChipProps {
  count: LeaveUsageCount;
  value: number | undefined;
}

function UsageChip({ count, value }: UsageChipProps) {
  return (
    <li className={cn(USAGE_CHIP_CLASS, value === 0 && ZERO_COUNT_CHIP_CLASS)}>
      {value === undefined ? (
        <Skeleton className="h-4 w-4 self-center" />
      ) : (
        <>
          <span className="text-base font-semibold text-foreground tabular-nums">{value}</span>
          <span className="sr-only">{pluralize(value, count.unit)}</span>
        </>
      )}
      {count.label}
    </li>
  );
}

interface LeaveUsagePeriodPanelProps {
  applicationId: number;
  period: UsagePeriod;
  leaveTypes: LeaveType[] | undefined;
}

function LeaveUsagePeriodPanel({ applicationId, period, leaveTypes }: LeaveUsagePeriodPanelProps) {
  const config = USAGE_PERIOD_CONFIG[period];
  const [pickedYear, setPickedYear] = useState<number | undefined>();
  const [pickedMonth, setPickedMonth] = useState<number | undefined>();
  const params = useMemo(
    () => (config.hasMonthPicker ? { year: pickedYear, month: pickedMonth } : { year: pickedYear }),
    [config.hasMonthPicker, pickedYear, pickedMonth],
  );

  const {
    data: summary,
    error,
    isFetching,
    isPlaceholderData,
    refetch,
  } = useQuery(leaveUsageSummaryQueryOptions(applicationId, params));

  const yearOptions = useMemo(() => getUsageYearOptions(summary?.year), [summary?.year]);

  const pickers = (
    <div className="flex flex-wrap items-center gap-0.5">
      {config.hasMonthPicker ? (
        <PeriodPicker
          id={`${period}-usage-month`}
          label="Month"
          options={MONTH_OPTIONS}
          value={pickedMonth ?? summary?.month}
          onChange={setPickedMonth}
        />
      ) : null}
      <PeriodPicker
        id={`${period}-usage-year`}
        label="Year"
        options={yearOptions}
        value={pickedYear ?? summary?.year}
        onChange={setPickedYear}
      />
    </div>
  );

  return (
    <DetailPanel
      title={config.title}
      icon={config.icon}
      actions={pickers}
      hasTintedHeader
      headerTint="flat"
      size="compact"
    >
      {error && !summary ? (
        <ErrorState
          error={error}
          fallbackTitle="Could not load the leave usage"
          onRetry={() => void refetch()}
          isRetrying={isFetching}
          size="compact"
        />
      ) : (
        <UsageChips
          usage={summary ? config.getUsage(summary) : undefined}
          leaveTypes={leaveTypes}
          isRefreshing={isPlaceholderData}
        />
      )}
    </DetailPanel>
  );
}

interface LeaveUsageSummaryProps {
  applicationId: number;
}

export function LeaveUsageSummary({ applicationId }: LeaveUsageSummaryProps) {
  const { data: leaveTypes, isError: hasLeaveTypesError } = useQuery(leaveTypesQueryOptions());
  const shownLeaveTypes = leaveTypes ?? (hasLeaveTypesError ? NO_LEAVE_TYPES : undefined);

  return (
    <div className="@container">
      <div className="grid gap-4 @[66rem]:grid-cols-2">
        <LeaveUsagePeriodPanel
          applicationId={applicationId}
          period="year"
          leaveTypes={shownLeaveTypes}
        />
        <LeaveUsagePeriodPanel
          applicationId={applicationId}
          period="month"
          leaveTypes={shownLeaveTypes}
        />
      </div>
    </div>
  );
}
